import { assertNever } from "@@/shared/assert-never";
import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import type { SocketEventBase } from "@@/shared/socket-events/socket-event-base";
import { createSocketEventBase } from "@@/shared/socket-events/socket-event-base";
import { exists } from "@@/utilities/exists";
import z from "zod";
import type { CheckInEvent } from "../reservation-check-ins/events/check-in.event";
import { statusEventIdZodSchema } from "../status-event-id";
import { statusReferencesZodObject } from "../status-references";
import type { StatusEventSocketTypeName } from "./status-event.socket-type-name";

type Name = "check-in";

const eventDataZodObject = z.object({
    _happenedAt: isoAndUnixZodSchema,
    status: z.enum(["checked-in", "checked-out", "pending"]),
    statusEventId: statusEventIdZodSchema,
    references: statusReferencesZodObject,
});
type EventData = z.infer<typeof eventDataZodObject>;
type CheckInSocketEvent = SocketEventBase<
    StatusEventSocketTypeName,
    Name,
    EventData
>;

const checkInSocketEventFactory = (
    checkInEvent: CheckInEvent,
): CheckInSocketEvent => {
    return createSocketEventBase<StatusEventSocketTypeName, Name, EventData>({
        type: "status-event:check-in",
        triggeredBy: checkInEvent.references.userId || "unknown",
        for: [
            checkInEvent.references.providerId,
            checkInEvent.references.userId,
            checkInEvent.references.orderId,
            checkInEvent.references.resourceReservationId,
            checkInEvent.references.sessionId,
            checkInEvent.references.resourceId,
        ].filter(exists),
        triggeredAt: checkInEvent._createdAt,
        data: {
            _happenedAt: checkInEvent._createdAt,
            status: (() => {
                switch (checkInEvent.type) {
                    case "CHECK_IN__INITIATED":
                    case "CHECK_IN__PENDING":
                        return "pending";
                    case "CHECK_IN__CHECKED_IN":
                        return "checked-in";
                    case "CHECK_IN__CHECKED_OUT":
                        return "checked-out";
                    default:
                        assertNever(
                            checkInEvent,
                            `Unknown check-in event type`,
                        );
                }
            })(),
            statusEventId: checkInEvent._id,
            references: checkInEvent.references,
        } satisfies EventData,
    });
};

export {
    eventDataZodObject as checkInSocketEventDataZodObject,
    checkInSocketEventFactory,
};
export type { CheckInSocketEvent };
